
import { useState } from 'react';

interface BillInputProps {
  monthlyBill: number;
  setMonthlyBill: (val: number) => void;
  homeSize: number;
  setHomeSize: (val: number) => void;
  currentSeer: number;
  setCurrentSeer: (val: number) => void;
}

export default function BillInput({
  monthlyBill,
  setMonthlyBill,
  homeSize,
  setHomeSize,
  currentSeer,
  setCurrentSeer,
}: BillInputProps) {
  const [billFocused, setBillFocused] = useState(false);

  const clamp = (value: number, min: number, max: number, fallback: number) => {
    if (Number.isNaN(value)) return fallback;
    return Math.min(Math.max(value, min), max);
  };

  return (
    <div className="space-y-6">
      {/* ==== Monthly Bill ==== */}
      <div className="bg-gradient-to-br from-brand-red-light to-brand-red-light/60 rounded-2xl p-6 border border-brand-red/20">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-red-light">
            <i className="ri-money-dollar-circle-line text-brand-red text-xl"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">
              Your Current Monthly Utility Bill
            </h3>
            <p className="text-xs text-gray-500">
              Enter the total amount from your electric bill
            </p>
          </div>
        </div>

        <div
          className={`relative rounded-xl border-2 transition-all duration-300 ${
            billFocused
              ? 'border-brand-red shadow-lg shadow-brand-red/10'
              : 'border-gray-200'
          }`}
        >
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">
            $
          </span>
          <input
            type="number"
            min={50}
            max={1000}
            value={monthlyBill}
            onChange={(e) => {
              const raw = Number(e.target.value);
              const safe = clamp(raw, 50, 1000, 50);
              setMonthlyBill(safe);
            }}
            onFocus={() => setBillFocused(true)}
            onBlur={() => setBillFocused(false)}
            className="w-full pl-10 pr-16 py-4 text-3xl font-bold text-gray-900 bg-white rounded-xl focus:outline-none text-sm"
            style={{ fontSize: '1.75rem' }}
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">
            /month
          </span>
        </div>

        <div className="mt-3">
          <input
            type="range"
            min={50}
            max={1000}
            step={10}
            value={monthlyBill}
            onChange={(e) => setMonthlyBill(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-red"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>$50</span>
            <span>$1,000</span>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
          <i className="ri-information-line"></i>
          Florida average: ~$180/month (1,080 kWh at $0.157/kWh)
        </p>
      </div>

      {/* ==== Home Size ==== */}
      <div className="bg-brand-cream rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-navy-light">
            <i className="ri-home-4-line text-brand-navy text-xl"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Home Size</h3>
            <p className="text-xs text-gray-500">Approximate square footage</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {[1200, 1800, 2400, 3200].map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => setHomeSize(size)}
              className={`py-3 px-2 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                homeSize === size
                  ? 'bg-brand-navy text-white shadow-lg'
                  : 'bg-white border border-gray-200 text-gray-700 hover:border-brand-navy/40'
              }`}
            >
              {size.toLocaleString()} ft²
            </button>
          ))}
        </div>
      </div>

      {/* ==== Current SEER ==== */}
      <div className="bg-brand-cream rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-navy-light">
            <i className="ri-temp-cold-line text-brand-navy text-xl"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">
              Current AC Efficiency (SEER)
            </h3>
            <p className="text-xs text-gray-500">
              Older units are typically 8-12 SEER
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <input
            type="range"
            min={8}
            max={18}
            step={1}
            value={currentSeer}
            onChange={(e) => setCurrentSeer(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-navy"
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">8 SEER (Old)</span>
            <span className="text-xl font-bold text-brand-navy">
              {currentSeer} SEER
            </span>
            <span className="text-xs text-gray-500">18 SEER (Newer)</span>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <div
              className={`h-2 flex-1 rounded-full ${
                currentSeer <= 10
                  ? 'bg-red-400'
                  : currentSeer <= 14
                  ? 'bg-brand-gold'
                  : 'bg-green-400'
              }`}
            ></div>
            <span
              className={`text-xs font-semibold ${
                currentSeer <= 10
                  ? 'text-red-500'
                  : currentSeer <= 14
                  ? 'text-brand-gold'
                  : 'text-green-600'
              }`}
            >
              {currentSeer <= 10
                ? 'Very Inefficient'
                : currentSeer <= 14
                ? 'Average'
                : 'Above Average'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
